import { useTranslation } from "react-i18next";
import {
  cn,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nexus/ui";
import type { EventActionItem } from "@/features/events/api/events.api";
import { getActionWaitDisplay } from "../lib/event-action-display";
import { getPublishedDisplay } from "../lib/event-row-display";
import { eventTopicLabel } from "../lib/event-topic-label";
import { eventTypeLabel } from "../lib/event-type-label";
import { formatPreciseDurationMs } from "../lib/event-timing";
import { EventActorCell } from "./shared/EventActorCell";
import { EventStatusBadge } from "./shared/EventStatusBadge";

interface EventActionsTableProps {
  rows: EventActionItem[];
  /** Ticks while an action is unfinished, so its running total keeps climbing. */
  now: number;
  /** An unfinished action older than this is reported as overdue, not as slow. */
  slowAfterMs: number;
  onSelectRow: (row: EventActionItem) => void;
  selectedCorrelationId?: string | null;
}

const TOPIC_SUBTITLE = "text-[10px] tracking-wide text-muted-foreground";

export function EventActionsTable({
  rows,
  now,
  slowAfterMs,
  onSelectRow,
  selectedCorrelationId = null,
}: EventActionsTableProps) {
  const { t, i18n } = useTranslation();

  return (
    <Table className="table-fixed">
      <colgroup>
        <col className="w-[110px]" />
        <col className="w-[112px]" />
        <col />
        <col className="w-[150px]" />
        <col className="w-[90px]" />
        <col className="w-[120px]" />
        <col className="w-9" />
      </colgroup>
      <TableHeader className="sticky top-0 z-10">
        <TableRow className="hover:bg-muted">
          <TableHead>{t("admin.events.actions.table.started", "Started")}</TableHead>
          <TableHead>{t("admin.events.table.status", "Status")}</TableHead>
          <TableHead>{t("admin.events.actions.table.action", "Action")}</TableHead>
          <TableHead>{t("admin.events.table.user", "User")}</TableHead>
          <TableHead>{t("admin.events.actions.table.events", "Events")}</TableHead>
          <TableHead>{t("admin.events.actions.table.waited", "Waited")}</TableHead>
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const started = getPublishedDisplay(row.startedAt, i18n.language);
          const wait = getActionWaitDisplay(row, now, slowAfterMs);
          const isFailed = row.status === "FAILED";

          return (
            <TableRow
              key={row.correlationId}
              onClick={() => onSelectRow(row)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelectRow(row);
                }
              }}
              tabIndex={0}
              aria-label={t(
                "admin.events.actions.table.rowLabel",
                "Open {{eventType}} and its events",
                { eventType: eventTypeLabel(t, row.eventType) },
              )}
              data-state={selectedCorrelationId === row.correlationId ? "selected" : undefined}
              className={cn(
                "cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
                isFailed && "bg-error-subtle",
              )}
            >
              <TableCell className="whitespace-nowrap">
                <div className="tabular-nums">{started.time}</div>
                <div className="text-[11px] text-muted-foreground">{started.date}</div>
              </TableCell>
              <TableCell>
                <EventStatusBadge status={row.status} />
              </TableCell>
              <TableCell className="min-w-0">
                <div className="truncate" title={row.eventType}>
                  {eventTypeLabel(t, row.eventType)}
                </div>
                <div className={cn(TOPIC_SUBTITLE, "truncate")} title={row.topic}>
                  {eventTopicLabel(t, row.topic)}
                </div>
              </TableCell>
              <TableCell className="min-w-0">
                <EventActorCell
                  name={row.actorUserName ?? row.actorUserId}
                  photoUrl={row.actorUserPhotoUrl}
                />
              </TableCell>
              <TableCell className="whitespace-nowrap tabular-nums">
                {row.jobCount}
                {row.failedCount > 0 ? (
                  <span className="ml-1 text-error">
                    {t("admin.events.actions.table.failedCount", "({{count}} failed)", {
                      count: row.failedCount,
                    })}
                  </span>
                ) : null}
              </TableCell>
              <TableCell
                className={cn(
                  "whitespace-nowrap tabular-nums",
                  wait.isUnfinished && "text-info",
                  wait.isOverdue && "text-warning",
                )}
                title={t(
                  "admin.events.actions.table.waitedHint",
                  "From the click, so time spent queued counts",
                )}
              >
                {formatPreciseDurationMs(wait.elapsedMs)}
                {wait.isUnfinished ? "…" : ""}
              </TableCell>
              <TableCell className="px-2 text-muted-foreground">
                <Icon name="chevron-right" size="xs" aria-hidden />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
